import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtInt } from "@/lib/api";
import { saveOrDownload } from "@/lib/downloadFolder";
import { applyDiagonalWatermark, hexToRgb } from "@/lib/pdfBranding";
import { buildBudgetRows } from "@/lib/budgetRows";

const eur = (v) => `€ ${fmtInt(v)}`;
const fmtDelta = (v) => (v === null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${eur(Math.abs(v))}`);
const fmtPct = (v) => (v === null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`);

// Vergelijkings-PDF: laatste begroting (Y) naast begroting Y-1 (indien aanwezig) en het laatste resultaat ≤ Y-1.
export async function downloadBudgetComparePdf({ t, club, clubId, budgets, historicals, subposts, userTier }) {
  const sorted = [...budgets].sort((a, b) => b.year - a.year);
  const target = sorted[0];
  const targetPrev = sorted.find((b) => b.year === target.year - 1) || null;
  const histRef = [...(historicals || [])].filter((h) => h.year < target.year).sort((a, b) => b.year - a.year)[0] || null;
  const base = targetPrev || histRef;

  const doc = new jsPDF({ orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();
  const brandRgb = hexToRgb(club?.brand_color || "#0047FF");
  doc.setFillColor(10, 10, 10);
  doc.rect(0, 0, pageW, 50, "F");
  let logo = false;
  if (club?.logo_data_uri) {
    try { doc.addImage(club.logo_data_uri, "AUTO", 15, 12, 24, 24, undefined, "FAST"); logo = true; } catch { /* fallback hieronder */ }
  }
  if (!logo) { doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]); doc.rect(15, 14, 8, 22, "F"); }
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text("MEERJARENPLAN ONLINE · BEGROTING", logo ? 44 : 28, 22);
  doc.setFontSize(18);
  doc.text(club?.name || "Club", logo ? 44 : 28, 32);
  doc.setTextColor(10, 10, 10);
  doc.setFontSize(11);
  const today = new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(today, pageW - 15 - doc.getTextWidth(today), 44);
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(`${t("Begroting")} ${target.year}${base ? ` · ${t("verschil t.o.v.")} ${targetPrev ? t("begroting") : t("resultaat")} ${base.year}` : ""}`, 14, 56);

  const head = [[
    t("Post"),
    ...(histRef ? [`${t("Resultaat")} ${histRef.year}`] : []),
    ...(targetPrev ? [`${t("Begroting")} ${targetPrev.year}`] : []),
    `${t("Begroting")} ${target.year}`,
    t("Verschil €"), t("Verschil %"),
  ]];
  const rows = buildBudgetRows(t, subposts, [histRef, targetPrev, target].filter(Boolean));
  const body = rows.map((row) => {
    const vT = row.value(target);
    const vB = base ? row.value(base) : null;
    const dE = vB === null || vT === null ? null : vT - vB;
    const dP = vB ? ((vT - vB) / Math.abs(vB)) * 100 : null;
    const cell = (v) => (v === null ? "—" : eur(v));
    return {
      kind: row.kind, strong: row.strong,
      cells: [
        row.kind === "sub" || row.kind === "sys" ? `    ${row.label}` : row.label,
        ...(histRef ? [cell(row.value(histRef))] : []),
        ...(targetPrev ? [cell(row.value(targetPrev))] : []),
        cell(vT), fmtDelta(dE), fmtPct(dP),
      ],
    };
  });
  autoTable(doc, {
    startY: 60,
    head, body: body.map((r) => r.cells),
    styles: { fontSize: 8.5, halign: "right" },
    headStyles: { fillColor: [10, 10, 10], halign: "center", fontSize: 8.5 },
    columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 52 } },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const meta = body[data.row.index];
      if (!meta) return;
      if (meta.kind === "sub" || meta.kind === "sys") {
        data.cell.styles.fontStyle = "italic";
        data.cell.styles.textColor = [90, 90, 90];
      } else if (meta.kind === "computed") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = meta.strong ? [230, 236, 255] : undefined;
      }
    },
  });

  let y = ((doc.lastAutoTable && doc.lastAutoTable.finalY) || 60) + 8;
  const wrapW = pageW - 28;
  const addNotes = (label, text) => {
    if (!text || !text.trim()) return;
    const lines = doc.splitTextToSize(text.trim(), wrapW);
    if (y + 6 + lines.length * 4 > doc.internal.pageSize.getHeight() - 14) { doc.addPage(); y = 20; }
    doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(label, 14, y);
    doc.setTextColor(0, 0, 0); doc.text(lines, 14, y + 4);
    y += 4 + lines.length * 4 + 6;
  };
  if (targetPrev?.notes) addNotes(`${t("Toelichting")} · ${t("Begroting")} ${targetPrev.year}:`, targetPrev.notes);
  if (target?.notes) addNotes(`${t("Toelichting")} · ${t("Begroting")} ${target.year}:`, target.notes);
  if (userTier === "TRIAL") applyDiagonalWatermark(doc, "PROEF");

  const filename = `${(club?.name || "club").replace(/[\\/:*?"<>|]/g, "-")}-begroting-${target.year}.pdf`;
  await saveOrDownload(doc.output("blob"), filename, clubId);
}
